/** @jsxImportSource @emotion/react */

import type { ProjectStateLifecycle } from '@/Client/Layout';
import { useEditableGroupLabel } from '@/Client/Lib';
import { getGroupTitle, getPartCostValue } from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import { formatUsd } from '@/Client/Presenters';
import { useScanEditingContextSlice } from '@/Client/State';
import { type ScanGroupOptimized } from '@kaiizen/kai-scan-template-system';

import { MetaList } from '@/Client/Widgets/MetaList';
import { pluralize } from '@art-suite/art-core-ts';
import { TagIcon } from '../../Widgets/StyledIcons/TagIcon';
import { getGroupOverviewTasks } from './focusedGroupOverviewLib';
import {
  OVERVIEW_HEADER_AMOUNT_STYLE,
  OVERVIEW_HEADER_BOX_STYLE,
  OVERVIEW_HEADER_LABEL_ICON_BUTTON_STYLE,
  OVERVIEW_HEADER_LABEL_INPUT_STYLE,
  OVERVIEW_HEADER_TITLE_BLOCK_STYLE,
  OVERVIEW_HEADER_TITLE_LINE_STYLE,
  OVERVIEW_HEADER_TITLE_ROW_STYLE,
  OVERVIEW_HEADER_TITLE_STYLE,
} from './focusedOverviewHeaderStyles';

export const CUSTOM_GROUP_LABEL_FIELD_LABEL = 'Custom group label';
export const CUSTOM_GROUP_LABEL_PLACEHOLDER = 'e.g. including Pantry';
export const ADD_GROUP_LABEL_TEXT = 'Add label';
export const EDIT_GROUP_LABEL_TEXT = 'Edit label';

/**
 * The group overview box, per the ShopEdit prototype's group page: the group's name
 * over "Group N of M · N tasks", the total right, then contractors beside the status
 * rollup — the same box the scope draws, holding a group's numbers.
 */
export const FocusedGroupOverviewHeader = ({
  group,
  groups = [],
  projectStateLifecycle,
}: {
  group: ScanGroupOptimized;
  /** Every group in the scan, in display order — names this group's place in the walk. */
  groups?: readonly ScanGroupOptimized[];
  projectStateLifecycle: ProjectStateLifecycle;
}) => {
  const {
    scanDataMutators,
    scanIsEditable,
  } = useScanEditingContextSlice([
    'scanDataMutators',
    'scanIsEditable',
  ]);
  const { beginEditing, cancelOrCommitOnKey, commitUnlessJustDismissed, draftSuffix, editing, updateDraftSuffix } = useEditableGroupLabel({
    commitCustomLabel: value =>
      scanDataMutators.updateGroup(group.id, {
        customLabel: value.trim() ? value.trim() : (null as unknown as undefined),
      }),
    group,
    scanIsEditable,
  });

  const groupTitle = getGroupTitle(group, 'Untitled group');
  const tasks = getGroupOverviewTasks(group);
  const groupIndex = groups.findIndex(candidate => candidate.id === group.id);

  return (
    <div css={OVERVIEW_HEADER_BOX_STYLE} data-testid='focused-group-overview-header'>
      <div css={OVERVIEW_HEADER_TITLE_ROW_STYLE}>
        <div css={OVERVIEW_HEADER_TITLE_BLOCK_STYLE}>
          <div css={OVERVIEW_HEADER_TITLE_LINE_STYLE}>
            <h2 css={OVERVIEW_HEADER_TITLE_STYLE}>{groupTitle}</h2>
            {scanIsEditable && (
              editing
                ? (
                  <input
                    aria-label={CUSTOM_GROUP_LABEL_FIELD_LABEL}
                    autoFocus
                    css={OVERVIEW_HEADER_LABEL_INPUT_STYLE}
                    maxLength={40}
                    placeholder={CUSTOM_GROUP_LABEL_PLACEHOLDER}
                    value={draftSuffix}
                    onBlur={commitUnlessJustDismissed}
                    onChange={event => updateDraftSuffix(event.target.value)}
                    onKeyDown={cancelOrCommitOnKey}
                  />
                )
                : (
                  <button
                    type='button'
                    aria-label={group.customLabel ? EDIT_GROUP_LABEL_TEXT : ADD_GROUP_LABEL_TEXT}
                    title={group.customLabel ? EDIT_GROUP_LABEL_TEXT : ADD_GROUP_LABEL_TEXT}
                    css={OVERVIEW_HEADER_LABEL_ICON_BUTTON_STYLE}
                    onClick={beginEditing}
                  >
                    <TagIcon size={14} />
                  </button>
                )
            )}
          </div>
          <MetaList
            parts={[
              ...groupIndex >= 0 ? [`Group ${groupIndex + 1} of ${groups.length}`] : [],
              pluralize(tasks.length, 'task'),
            ]}
          />
        </div>
        <span css={OVERVIEW_HEADER_AMOUNT_STYLE}>
          {formatUsd(getPartCostValue(group) ?? 0, false, true)}
        </span>
      </div>
    </div>
  );
};
